import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsLookbookEnabled } from '@app/ducks';
import { getMarketName, getOwnerName } from '@app/logic/lookbook';
import { compiledPaths, paths } from '@app/paths';
import { useLookbooksList } from '@app/services/hooks/lookbook/useLookbooksList';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useIsScreenSize, useModalVisibility, usePageScrolling, useUrlPagination } from '@app/utils/hooks';
import { usePaginationHandler } from '@app/utils/hooks/table/usePaginationHandler';
import { useTable } from '@app/utils/hooks/table/useTable';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { renderStandardCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { Lookbook } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as ReactLink, Navigate } from 'react-router-dom';

import { Button } from '../various/Button';
import { DelwinNameAndDate } from '../various/DelwinNameAndDate';
import { PageTitle } from '../various/PageTitle';
import { Pagination } from '../various/Pagination';
import { CollapsibleListCell, Table } from '../various/Table';
import { TableControls } from '../various/TableControls';

import { LookbookListingContextProvider } from './context/LookbookListingContext';
import { CreateLookbookModal } from './CreateLookbookModal';
import { LookbookCard } from './LookbookCard';
import { LookbookCardSkeleton } from './LookbookCardSkeleton';
import { LookbookEmptyState } from './LookbookEmptyState';
import { LookbookNameColumn } from './LookbookNameColumn';
import { LookbookOptionsColumn } from './LookbookOptionsColumn';
import { LookbookSettingsModal } from './LookbookSettingsModal';

const columnHelper = createColumnHelper<Lookbook.ListingItem>();

export const LookbooksPage = () => {
  const { t } = useTranslation(['common', 'lookbook']);
  const isLookbookEnabled = useSelector(getIsLookbookEnabled);
  const { scrollToTopDelay } = usePageScrolling();
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'lookbook' });
  const { showModal } = useModalVisibility('CreateLookbookModal');
  const [page, setPage] = useUrlPagination();
  const { data, isLoading, isError } = useLookbooksList({ page });
  const { trackEvent } = useAmplitude();
  const pageData = data?.data;
  const totalCount = data?.totalCount ?? 0;
  const tableData = pageData?.lookbooks ?? [];
  const allDeliveryWindows = React.useMemo(() => pageData?.deliveryWindows ?? [], [pageData?.deliveryWindows]);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const paginationInfo = {
    current: page,
    total: totalCount,
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    scrollToTopDelay();
  };
  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });

  const markets = React.useMemo(() => pageData?.markets ?? [], [pageData?.markets]);
  const owners = React.useMemo(() => pageData?.owners ?? [], [pageData?.owners]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('market', {
        cell: info => {
          const path = compiledPaths.LOOKBOOK_SINGLE({ lookbookId: info.row.original.lookbook });

          return (
            <ReactLink to={path} className="rounded-regular body-1 absolute-cursor-pointer focus-visible:focus-outline">
              {getMarketName(markets, info.getValue())}
            </ReactLink>
          );
        },
        enableHiding: false,
        header: t('common:market'),
      }),
      columnHelper.accessor('name', {
        cell: info => <LookbookNameColumn id={info.row.original.lookbook} name={info.row.original.name} />,
        header: t('common:name'),
        meta: {
          noWrap: true,
          width: 100,
        },
      }),
      columnHelper.accessor('deliveryWindows', {
        cell: info => {
          const path = compiledPaths.LOOKBOOK_SINGLE({ lookbookId: info.row.original.lookbook });
          const deliveryWindowIds = info.getValue();
          const deliveryWindows = deliveryWindowIds
            .map(id => allDeliveryWindows.find(delwin => delwin.deliveryWindow === id))
            .filter(isDefined);

          return (
            <ReactLink to={path} tabIndex={-1} className="contents">
              {isEmpty(deliveryWindows) ?
                <span className="pl-1">{t('common:all')}</span>
              : <CollapsibleListCell
                  items={deliveryWindows.map(deliveryWindow => (
                    <DelwinNameAndDate key={deliveryWindow.deliveryWindow} insertCommas deliveryWindow={deliveryWindow} />
                  ))}
              />
              }
            </ReactLink>
          );
        },
        header: t('common:delivery_window_other'),
        meta: {
          isCollapsible: true,
        },
      }),
      columnHelper.accessor('owner', {
        cell: info => getOwnerName(owners, info.getValue()),
        header: t('common:owner'),
        meta: {
          noWrap: true,
          width: 100,
        },
      }),
      columnHelper.accessor('dateCreated', {
        cell: renderStandardCell,
        header: t('common:created'),
        meta: {
          width: 1,
        },
      }),
      columnHelper.accessor('dateLastVisited', {
        cell: renderStandardCell,
        header: t('common:last_visited'),
        meta: {
          width: 1,
        },
      }),
      columnHelper.accessor('visitedCount', {
        cell: renderStandardCell,
        header: t('common:visited_count'),
        meta: {
          align: 'right',
          width: 1,
        },
      }),
      columnHelper.display({
        cell: ({ row }) => <LookbookOptionsColumn lookbookId={row.original.lookbook} />,
        enableHiding: false,
        id: 'actions',
        meta: {
          align: 'center',
          width: 1,
        },
      }),
    ],
    [t, allDeliveryWindows, markets],
  );

  const shouldShowTable = isLoading || !isEmpty(tableData);
  const table = useTable(
    {
      columns,
      data: tableData,
      enableSorting: false,
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      onPaginationChange: handlePaginationChange,
      state: {
        pagination,
      },
    },
    columnsVisibility,
  );

  if (!isLookbookEnabled) {
    return <Navigate to={paths.ROOT} replace />;
  }

  const handleColumnsChange = (column: string, isChecked: boolean) => {
    if (!isChecked) {
      trackEvent({ name: 'lookbookTable.multiselect.column.unchecked', properties: { column } });
    }
  };

  return (
    <LookbookListingContextProvider>
      <PageTitle title={t('common:lookbooks')}>
        <div className="flex flex-1 flex-col px-4 lg:px-8">
          <div className="sticky top-[--headerHeight] z-topBar mb-2 flex items-center justify-between gap-2 bg-neutral-96 py-2 transition-all group-[.scrollingDown]/body:top-0 lg:mb-0">
            <div className="flex items-center gap-8 only:w-full">
              <h2 className="flex-1 heading-3 sm:flex-initial">
                {t('common:lookbooks')}
                {!isLoading && <span className="text-neutral-40"> ({tableData.length})</span>}
              </h2>
              {shouldShowTable && (
                <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="lookbook" onChange={handleColumnsChange} />
              )}
            </div>
            {!isMobile && shouldShowTable && <Button onClick={showModal}>{t('lookbook:create_lookbook')}</Button>}
          </div>
          {shouldShowTable ?
            <>
              <Table
                table={table}
                isLoading={isLoading}
                renderSkeletonCard={index => <LookbookCardSkeleton key={index} />}
                renderCard={row => <LookbookCard row={row} markets={markets} owners={owners} deliveryWindows={allDeliveryWindows} />}
              />
              <Pagination
                onChange={table.setPageIndex}
                current={paginationInfo.current}
                totalItems={paginationInfo.total}
                onActionButtonClick={showModal}
                actionButtonLabel={t('lookbook:create_lookbook')}
                isHidden={isLoading}
              />
            </>
          : <LookbookEmptyState isError={isError} />}
          <CreateLookbookModal />
          <LookbookSettingsModal />
        </div>
      </PageTitle>
    </LookbookListingContextProvider>
  );
};
